import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RadioGroup, RadioGroupChangeEvent } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import AttachIcon from 'src/assets/icons/AttachIcon';
import UnAttachIcon from 'src/assets/icons/UnAttachIcon';
import Button from 'src/components/design-system/Button';
import {
  NO_REQUIRED_SUPPLIER_FIELDS,
  SupplierEmailValidationStatus,
  SupplierFieldAccess,
  SupplierFormFieldKey,
  SupplierFormValues,
  SupplierRequiredFields,
  SupplierTransmissionType,
} from 'src/types/manage-suppliers/SupplierForm';
import SupplierFormField, { SUPPLIER_FORM_TOOLTIP_ID } from './SupplierFormField';
import SupplierStatusField from './SupplierStatusField';
import {
  getOrderEmailValidationUi,
  getTransmissionValidationMessage,
  shouldShowTransmissionApprovalMessage,
} from './utils/orderEmailValidationUi';
import {
  isSupplierFieldOverMaxLength,
  isSupplierLengthValidatableField,
} from './utils/supplierFieldLimits';
import {
  EMPTY_SUPPLIER_DISPLAY_VALUE,
  getSupplierRequiredFieldMarkers,
  toSupplierPhoneValue,
} from './utils/supplierFormValues';

interface SupplierFormProps {
  mode: 'create' | 'edit';
  values: SupplierFormValues;
  errors: Partial<Record<SupplierFormFieldKey, string>>;
  touched: Partial<Record<SupplierFormFieldKey, boolean>>;
  fieldAccess: SupplierFieldAccess;
  /** Fields required by permission (InvoiceRequireApId / InvoiceRequireExternalSupplierName). */
  requiredFields?: SupplierRequiredFields;
  onFieldChange: (field: SupplierFormFieldKey, value: string | SupplierTransmissionType) => void;
  onFieldBlur: (field: SupplierFormFieldKey) => void;
  ownershipBanner?: string;
  /** Whether the panel already renders an action bar (Edit/Save/Cancel) above this form, which
   * provides its own vertical spacing below the panel header. */
  hasActionBar?: boolean;
  /** `true` = unlinked (UnAttach), `false` = linked (Attach), omit = hide icon. */
  showUnlinkedIndicator?: boolean;
  emailValidationStatus?: SupplierEmailValidationStatus;
  emailValidationDateTime?: string;
  emailValidationExpired?: boolean;
  onResendValidationRequest?: () => void;
  isResendingValidation?: boolean;
  showResendValidation?: boolean;
  showProgramInfo?: boolean;
  hasProgramInfo?: boolean;
  onProgramInfoClick?: () => void;
  showTransmissionSettings?: boolean;
}

const SupplierForm: FC<SupplierFormProps> = ({
  mode,
  values,
  errors,
  touched,
  fieldAccess,
  requiredFields = NO_REQUIRED_SUPPLIER_FIELDS,
  onFieldChange,
  onFieldBlur,
  ownershipBanner,
  hasActionBar = false,
  showUnlinkedIndicator,
  emailValidationStatus = 'none',
  emailValidationDateTime,
  emailValidationExpired = false,
  onResendValidationRequest,
  isResendingValidation = false,
  showResendValidation = false,
  showProgramInfo = false,
  hasProgramInfo = false,
  onProgramInfoClick,
  showTransmissionSettings = true,
}) => {
  const { t } = useTranslation();

  const transmissionOptions = useMemo(
    () => [
      { label: t('Log Only'), value: 'logOnly' },
      { label: t('Email'), value: 'email' },
    ],
    [t]
  );

  const transmissionLabel = useMemo(() => {
    const match = transmissionOptions.find((option) => option.value === values.transmissionType);
    const label = match?.label ?? values.transmissionType;
    return label.trim() ? label : EMPTY_SUPPLIER_DISPLAY_VALUE;
  }, [transmissionOptions, values.transmissionType]);

  const showFieldError = (field: SupplierFormFieldKey) => {
    if (!errors[field]) {
      return false;
    }

    if (
      isSupplierLengthValidatableField(field) &&
      isSupplierFieldOverMaxLength(field, values[field])
    ) {
      return true;
    }

    // Paired errors show right away once the other field has a value (as on Invoice Details).
    if (field === 'apId' && values.externalSupplierName.trim()) {
      return true;
    }

    if (field === 'externalSupplierName' && values.apId.trim()) {
      return true;
    }

    return Boolean(touched[field]);
  };

  const requiredMarkers = getSupplierRequiredFieldMarkers({
    values,
    requiredFields,
    hasSupplierInheritance: typeof showUnlinkedIndicator === 'boolean',
  });

  const getFieldError = (field: SupplierFormFieldKey) =>
    showFieldError(field) ? errors[field] : undefined;

  const isFieldReadOnly = (field: SupplierFormFieldKey) => !fieldAccess[field];

  const isOrderEmailEditable = fieldAccess.orderEmail;

  const orderEmailRequired = fieldAccess.orderEmail && values.transmissionType === 'email';

  const isOrderEmailReadOnly = !isOrderEmailEditable;

  const orderEmailValidationUi = getOrderEmailValidationUi({
    mode,
    values,
    emailValidationStatus,
    emailValidationDateTime,
    emailValidationExpired,
    isOrderEmailReadOnly,
    t,
  });

  const showTransmissionApprovalMessage = shouldShowTransmissionApprovalMessage({
    mode,
    values,
    emailValidationStatus,
    isOrderEmailReadOnly,
  });

  const transmissionValidationMessage = getTransmissionValidationMessage({
    mode,
    values,
    emailValidationStatus,
    emailValidationDateTime,
    emailValidationExpired,
    t,
  });

  const sectionBodyClassName = 'supplier-form-section__body';

  const apIdLinkTooltip = showUnlinkedIndicator
    ? t(
        'AP ID is currently not being inherited from corporate and is saved locally. To inherit delete both the AP ID and External Supplier Name and save'
      )
    : t(
        'Inherited from Management Company. Changing the value will cause both the AP ID and External Supplier Name to become unlinked'
      );

  const apIdLinkIcon =
    typeof showUnlinkedIndicator === 'boolean' ? (
      <span
        className="supplier-form__unlinked-icon"
        data-tooltip-id={SUPPLIER_FORM_TOOLTIP_ID}
        data-tooltip-content={apIdLinkTooltip}
        data-tooltip-place="bottom"
      >
        {showUnlinkedIndicator ? (
          <UnAttachIcon width="16" height="16" />
        ) : (
          <AttachIcon width="16" height="16" />
        )}
      </span>
    ) : null;

  return (
    <div className={clsx('supplier-form', !hasActionBar && 'pt-4')}>
      {ownershipBanner && (
        <div className="supplier-form__banner px-[32px] pb-3 text-sm text-gray-700">
          {ownershipBanner}
        </div>
      )}

      <div className="px-[32px] pb-6">
        <section className="supplier-form-section">
          <div className="supplier-form-section__header">{t('Supplier Info')}</div>
          <div className={sectionBodyClassName}>
            <div className="flex flex-col gap-4">
              <div
                className={clsx(
                  'grid grid-cols-1 gap-4 md:grid-cols-3',
                  mode === 'edit' && 'md:items-end'
                )}
              >
                {mode === 'edit' ? (
                  <>
                    <SupplierFormField
                      id="supplier-name"
                      label={t('Supplier Name')}
                      value={values.supplierName}
                      onChange={(value) => onFieldChange('supplierName', value)}
                      onBlur={() => onFieldBlur('supplierName')}
                      error={getFieldError('supplierName')}
                      required={fieldAccess.supplierName}
                      readOnly={isFieldReadOnly('supplierName')}
                    />
                    <SupplierStatusField
                      value={values.status}
                      onChange={(value) => onFieldChange('status', value)}
                      readOnly={isFieldReadOnly('status')}
                      className="supplier-form__status-field md:col-span-2"
                    />
                  </>
                ) : (
                  <SupplierFormField
                    id="supplier-name"
                    label={t('Supplier Name')}
                    value={values.supplierName}
                    onChange={(value) => onFieldChange('supplierName', value)}
                    onBlur={() => onFieldBlur('supplierName')}
                    error={getFieldError('supplierName')}
                    required={fieldAccess.supplierName}
                    readOnly={isFieldReadOnly('supplierName')}
                    className="md:col-span-3"
                  />
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <SupplierFormField
                  id="external-supplier-name"
                  label={t('External Supplier Name')}
                  value={values.externalSupplierName}
                  onChange={(value) => onFieldChange('externalSupplierName', value)}
                  onBlur={() => onFieldBlur('externalSupplierName')}
                  error={getFieldError('externalSupplierName')}
                  required={requiredMarkers.externalSupplierName}
                  readOnly={isFieldReadOnly('externalSupplierName')}
                  trailingContent={apIdLinkIcon}
                />
                <SupplierFormField
                  id="ap-id"
                  label={t('Accts. Payable ID #')}
                  value={values.apId}
                  onChange={(value) => onFieldChange('apId', value)}
                  onBlur={() => onFieldBlur('apId')}
                  error={getFieldError('apId')}
                  required={requiredMarkers.apId}
                  readOnly={isFieldReadOnly('apId')}
                  trailingContent={apIdLinkIcon}
                />
                <SupplierFormField
                  id="account-number"
                  label={t('Account #')}
                  value={values.accountNumber}
                  onChange={(value) => onFieldChange('accountNumber', value)}
                  error={getFieldError('accountNumber')}
                  readOnly={isFieldReadOnly('accountNumber')}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="supplier-form-section">
          <div className="supplier-form-section__header">{t('Supplier Address')}</div>
          <div className={sectionBodyClassName}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <SupplierFormField
                id="address-1"
                label={t('Address 1')}
                value={values.address1}
                onChange={(value) => onFieldChange('address1', value)}
                error={getFieldError('address1')}
                readOnly={isFieldReadOnly('address1')}
              />
              <SupplierFormField
                id="address-2"
                label={t('Address 2')}
                value={values.address2}
                onChange={(value) => onFieldChange('address2', value)}
                error={getFieldError('address2')}
                readOnly={isFieldReadOnly('address2')}
              />
              <SupplierFormField
                id="city"
                label={t('City')}
                value={values.city}
                onChange={(value) => onFieldChange('city', value)}
                error={getFieldError('city')}
                readOnly={isFieldReadOnly('city')}
              />
              <SupplierFormField
                id="state-province"
                label={t('State/Province')}
                value={values.stateProvince}
                onChange={(value) => onFieldChange('stateProvince', value)}
                error={getFieldError('stateProvince')}
                readOnly={isFieldReadOnly('stateProvince')}
              />
              <SupplierFormField
                id="zip-postal-code"
                label={t('Zip/Postal Code')}
                value={values.zipPostalCode}
                onChange={(value) => onFieldChange('zipPostalCode', value)}
                error={getFieldError('zipPostalCode')}
                readOnly={isFieldReadOnly('zipPostalCode')}
              />
              <SupplierFormField
                id="country"
                label={t('Country')}
                value={values.country}
                onChange={(value) => onFieldChange('country', value)}
                error={getFieldError('country')}
                readOnly={isFieldReadOnly('country')}
              />
            </div>
          </div>
        </section>

        <section className="supplier-form-section">
          <div className="supplier-form-section__header">{t('Contact Info')}</div>
          <div className={sectionBodyClassName}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <SupplierFormField
                id="contact-name"
                label={t('Contact Name')}
                value={values.contactName}
                onChange={(value) => onFieldChange('contactName', value)}
                error={getFieldError('contactName')}
                readOnly={isFieldReadOnly('contactName')}
              />
              <SupplierFormField
                id="phone"
                label={t('Phone')}
                value={values.phone}
                onChange={(value) => onFieldChange('phone', toSupplierPhoneValue(value))}
                onBlur={() => onFieldBlur('phone')}
                error={getFieldError('phone')}
                readOnly={isFieldReadOnly('phone')}
              />
              <SupplierFormField
                id="order-email"
                label={t('Order Email')}
                value={values.orderEmail}
                onChange={(value) => onFieldChange('orderEmail', value)}
                onBlur={() => onFieldBlur('orderEmail')}
                error={getFieldError('orderEmail')}
                required={orderEmailRequired}
                readOnly={isOrderEmailReadOnly}
                inputClassName={orderEmailValidationUi.inputClassName}
                showValidationErrorBorder={
                  orderEmailValidationUi.showValidationErrorBorder && !getFieldError('orderEmail')
                }
                helperText={
                  getFieldError('orderEmail') ? undefined : orderEmailValidationUi.helperText
                }
                helperClassName={orderEmailValidationUi.helperClassName}
              />
              {showProgramInfo && (
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">{t('Program Info')}</span>
                  {hasProgramInfo ? (
                    <button
                      type="button"
                      className="flex w-fit items-center gap-1 p-0 text-xs font-semibold text-info hover:underline focus:outline-none"
                      onClick={onProgramInfoClick}
                    >
                      {t('Click here')}
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
                    </button>
                  ) : (
                    <span className="supplier-form__field-value">
                      {EMPTY_SUPPLIER_DISPLAY_VALUE}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {showTransmissionSettings && (
          <section className="supplier-form-section">
            <div className="supplier-form-section__header">{t('PO Transmission Settings')}</div>
            <div className={sectionBodyClassName}>
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="supplier-form__transmission-type flex flex-col gap-2">
                  <span className="text-sm text-gray-500">{t('Transmission Type')}</span>
                  {isFieldReadOnly('transmissionType') ? (
                    <span className="supplier-form__field-value">{transmissionLabel}</span>
                  ) : (
                    <RadioGroup
                      data={transmissionOptions}
                      value={values.transmissionType}
                      onChange={(event: RadioGroupChangeEvent) => {
                        const nextValue = event.value as SupplierTransmissionType;
                        onFieldChange('transmissionType', nextValue);
                      }}
                      layout="horizontal"
                    />
                  )}
                  {showTransmissionApprovalMessage && (
                    <p className="text-sm font-normal text-success">
                      {t('Request will be sent to your order email for approval upon save.')}
                    </p>
                  )}
                  {transmissionValidationMessage && (
                    <p
                      className={clsx(
                        'text-sm font-normal',
                        transmissionValidationMessage.className
                      )}
                    >
                      {transmissionValidationMessage.text}
                    </p>
                  )}
                </div>
                {showResendValidation && onResendValidationRequest && (
                  <Button
                    color="info"
                    size="sm"
                    onClick={onResendValidationRequest}
                    loading={isResendingValidation}
                    className="shrink-0 self-start"
                  >
                    {t('Resend Request')}
                  </Button>
                )}
              </div>
            </div>
          </section>
        )}
      </div>

      <Tooltip
        id={SUPPLIER_FORM_TOOLTIP_ID}
        style={{
          backgroundColor: 'black',
          color: 'white',
          zIndex: 99999,
          maxWidth: '80%',
          wordWrap: 'break-word',
        }}
        place="bottom"
      />
    </div>
  );
};

export default SupplierForm;
